import {InputRightElement, ModalHeader} from '@chakra-ui/react';
import {unwrapResult} from '@reduxjs/toolkit';
import moment from 'moment';
import React, {memo, useEffect, useState} from 'react';
import {IoIosArrowBack, IoIosArrowForward} from 'react-icons/io';
import {RiArrowDropDownLine} from 'react-icons/ri';
import {useDispatch, useSelector} from 'react-redux';
import {
  AppModal,
  Col,
  Row,
  UiBox,
  UiButton,
  UiGridItem,
  UiIcon,
  UiInput,
  UiInputGroup,
  UiSimpleGrid,
  UiSpinner,
  UiText,
} from '~/components/elements';
import {APPOINTMENT_BOOKING_TYPE} from '~/constants/appointment-bookings';
import {useRequestState} from '~/hooks';
import {fetchAppointmentTime} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {setAppointmentTimeSelectModal} from '~/redux/ui/slice';
import {APPOINTMENT_WEEK_DATE_TIME_FORMAT, DATE_FORMAT, LAST_DAY_NEXT_MONTH, TIME_FORMAT} from '~/utils/helpers';
import {spacing} from '~/utils/theme/spacing';
import {requestOK} from '~/utils/thunk-api';

interface Props {
  variant?: string;
  headerTitle?: string;
  placeholder?: string;
  defaultValue?: Date;
  onChange?: (value?: Date) => void;
  onChangeDate?: (value?: Date) => void;
  date?: Date;
  showEndTime?: boolean;
  note?: string;
  showNoSlotWantToBook?: boolean;
  isPlanBBooking?: boolean;
}

export const UiInputTimePicker = memo(function UiInputTimePicker({
  variant,
  headerTitle,
  placeholder,
  defaultValue,
  onChange,
  date,
  onChangeDate,
  note,
  showEndTime = false,
  showNoSlotWantToBook = false,
  isPlanBBooking = false,
  ...rest
}: Props) {
  const dispatch: AppDispatch = useDispatch();
  const isShowModal = useSelector((state: RootState) => state.ui.appointmentTimeSelectModal.isShowing);
  const userQuestionnaireId = useSelector((state: RootState) => state.questionnaires.userQuestionnaireId);
  const {loading} = useRequestState(fetchAppointmentTime);
  const [currentDate, setCurrentDate] = useState<Date | undefined>(date || new Date());
  const [selectedValue, setSelectedValue] = useState(defaultValue);
  const [timeList, setTimeList] = useState<any[]>();
  const {userQuestionId} = useSelector((state: RootState) => state.questionnaires.rebookingAppointment);

  const handleModalVisible = () => {
    dispatch(setAppointmentTimeSelectModal());
    setSelectedValue(defaultValue);
    if (date) setCurrentDate(date);
  };

  const getTime = async () => {
    const questionnaireId = userQuestionnaireId || userQuestionId;
    if (questionnaireId) {
      const resAction = await dispatch(
        fetchAppointmentTime({
          date: moment(currentDate || new Date()).format(DATE_FORMAT),
          userQuestionnaireId: questionnaireId,
        }),
      );
      if (requestOK(resAction, fetchAppointmentTime)) {
        const result = unwrapResult(resAction);
        setTimeList(result);
      }
    }
  };

  useEffect(() => {
    if (currentDate) {
      getTime();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const onClickConfirm = () => {
    if (onChange) {
      onChange(selectedValue);
    }
    if (onChangeDate) {
      onChangeDate(currentDate);
    }
    handleModalVisible();
  };

  const onClickNextDay = () => {
    if (moment(currentDate).isSame(LAST_DAY_NEXT_MONTH, 'day')) return;
    setCurrentDate(moment(currentDate).add(1, 'day').toDate());
    setSelectedValue(undefined);
  };

  const onCLickPreviousDay = () => {
    if (moment(currentDate).isSame(moment(), 'day')) return;
    setCurrentDate(moment(currentDate).subtract(1, 'day').toDate());
    setSelectedValue(undefined);
  };

  const onClickNoSlot = () => {
    handleModalVisible();
    let postMessage: {
      func: string;
      message: string;
      type?: number;
    } = {
      func: 'triggerChat',
      message: 'Trigger chat from iframe.',
    };
    if (isPlanBBooking) {
      postMessage = {
        ...postMessage,
        type: +APPOINTMENT_BOOKING_TYPE.THIRTY_MINS,
      };
    }
    if (window.parent) {
      window.parent.postMessage(postMessage, '*');
    }
  };

  useEffect(() => {
    setSelectedValue(defaultValue);
    if (date) setCurrentDate(date);
  }, [defaultValue, date]);

  const renderTimeSelect = () => {
    if (loading)
      return (
        <UiBox
          height="255px"
          mt={spacing.smedium}
          width="100%"
          justifyContent="center"
          alignItems="center"
          display="flex"
        >
          <UiSpinner thickness="4px" speed="0.65s" emptyColor="lightestGrey" size="xl" />
        </UiBox>
      );
    if (timeList?.length) {
      return (
        <UiSimpleGrid
          templateColumns="repeat(4, 1fr)"
          gap={4}
          mt={spacing.smedium}
          height="255px"
          overflowY="scroll"
          px="5%"
          minChildWidth="80px"
          width="100%"
        >
          {timeList?.map((i: any, index: number) => {
            const oddArrayLastItem = index === timeList?.length - 1 && timeList?.length % 2 !== 0;
            const timeFormat = showEndTime ? 'hh:mm' : 'hh:mm A';
            const startTimeStr = moment(i.startTime).format(timeFormat);
            const endTimeStr = moment(i.endTime).format(`${timeFormat} A`);
            const title = showEndTime ? `${startTimeStr} - ${endTimeStr}` : startTimeStr;
            return (
              <UiGridItem
                key={i.startTime}
                colSpan={oddArrayLastItem ? 4 : 2}
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <UiButton
                  title={title}
                  variant={selectedValue === i.startTime ? 'solid' : 'outline'}
                  borderRadius={8}
                  width={oddArrayLastItem ? '50%' : '100%'}
                  onClick={() => setSelectedValue(i.startTime)}
                  minWidth="40%"
                />
              </UiGridItem>
            );
          })}
        </UiSimpleGrid>
      );
    }
    return (
      <UiBox
        height="255px"
        mt={spacing.smedium}
        width="100%"
        justifyContent="center"
        alignItems="center"
        display="flex"
      >
        <UiText textAlign="center">No slot available</UiText>
      </UiBox>
    );
  };

  return (
    <>
      <AppModal
        isOpen={isShowModal}
        onClose={handleModalVisible}
        isBottom
        closeButtonSize="lg"
        actions={null}
        header={
          <ModalHeader fontSize="xs" textAlign="center" mt={2}>
            {headerTitle}
          </ModalHeader>
        }
        autoFocus={false}
        motionPreset="slideInBottom"
        modalBodyProps={{px: 2}}
      >
        <Row width="100%" justifyContent="space-between" alignItems="center">
          <UiIcon as={IoIosArrowBack} boxSize={6} onClick={onCLickPreviousDay} />
          <UiText fontWeight="500" color="fadedJade">
            {moment(currentDate || moment()).format(APPOINTMENT_WEEK_DATE_TIME_FORMAT)}
          </UiText>
          <UiIcon as={IoIosArrowForward} boxSize={6} onClick={onClickNextDay} />
        </Row>
        {renderTimeSelect()}
        {!!note && (
          <UiText mt="25px" textAlign="center" fontSize="xs" color="liteOuter" noOfLines={3}>
            {note}
          </UiText>
        )}
        <Col>
          {showNoSlotWantToBook && (
            <Col mt={2} alignItems="center" justifyContent="center">
              <UiText
                textAlign="center"
                fontWeight="600"
                color="graySecondary"
                my={1}
                onClick={onClickNoSlot}
                cursor="pointer"
              >
                I don&apos;t see a slot that I want
              </UiText>
            </Col>
          )}

          <UiButton title="Confirm" mt="15px" mx="20%" onClick={onClickConfirm} disabled={!selectedValue} />
        </Col>
      </AppModal>
      <UiInputGroup>
        <UiInput
          borderRadius={8}
          size="lg"
          fontSize="md"
          placeholder={placeholder}
          isReadOnly
          onClick={() => dispatch(setAppointmentTimeSelectModal())}
          bg="white"
          defaultValue={defaultValue && moment(defaultValue).format(TIME_FORMAT)}
          {...rest}
        />
        <InputRightElement pointerEvents="none" mr={1} mt={2}>
          <UiIcon as={RiArrowDropDownLine} boxSize={8} />
        </InputRightElement>
      </UiInputGroup>
    </>
  );
});
